import React from 'react';
import { Flex, Tooltip, Typography } from 'antd';

import { useUIText } from '../../i18n';
import { useUIColors } from '../../theme';
import { cx, type Styleable } from '../../variants';

const { Title, Text } = Typography;

export interface PageHeaderProps extends Styleable {
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    actions?: React.ReactNode;
    breadcrumb?: React.ReactNode;
    level?: 1 | 2 | 3 | 4;
    /** Bottom spacing under the header block. Fit-to-shell pages pass `0` and
     *  rely on the page column `gap` instead (see accounts frame 72). */
    marginBottom?: number;
    /**
     * Collapse `subtitle` into an (i) beside the title instead of printing it.
     *
     * Default `true` (EAA-1846, design frame 98 "Vỏ chuẩn"): the title strip's
     * description is MANDATORY tooltip-only ("Mô tả không in ra dải tiêu đề"),
     * not opt-in — a page that still wants the old printed line (a genuinely
     * per-visit fact like a live count) sets `collapseSubtitle={false}`
     * explicitly, which is now the exception, not the default.
     */
    collapseSubtitle?: boolean;
    /**
     * EAA-1817: nodes that belong to the TITLE, printed after it and after the
     * (i) — the handoff's title strips carry a run of status pills there
     * (frames 43, 55, 55a: "41 đã liên kết / 96 · 52 chưa phủ · 1 tạm dừng · 2
     * app chưa sẵn sàng").
     *
     * Deliberately not `actions`: those are right-aligned controls, and a
     * caller that put the pills there got them shoved against the buttons on
     * the far side of the strip. Deliberately not folded into `title` either —
     * that renders them INSIDE the `<h1>`, which puts four counts into the
     * page's accessible name and makes the heading read as a sentence.
     */
    titleExtra?: React.ReactNode;
}

/**
 * Tier-1 presentational primitive: a page-level title block with an optional
 * breadcrumb line, subtitle, and a right-aligned actions slot. antd v5+
 * removed its own `PageHeader` component, so this is built from `Flex` and
 * `Typography` primitives instead of relying on that (now-gone) API.
 */
export function PageHeader({
    title,
    subtitle,
    actions,
    breadcrumb,
    level = 2,
    collapseSubtitle = true,
    titleExtra,
    marginBottom = 24,
    className,
    style,
}: PageHeaderProps): React.ReactElement {
    const colors = useUIColors();
    const text = useUIText();
    const collapsed = collapseSubtitle && Boolean(subtitle);

    return (
        <div className={cx('page-header', className)} style={{ marginBottom, ...style }}>
            {breadcrumb && <div style={{ marginBottom: 8 }}>{breadcrumb}</div>}

            <Flex justify="space-between" align="flex-start" gap={16} wrap="wrap">
                <div style={{ minWidth: 0 }}>
                    <Flex align="center" gap={8} wrap="wrap">
                        <Title level={level} style={{ margin: 0 }}>
                            {title}
                        </Title>
                        {collapsed && (
                            // EAA-1839: these five values used to be hardcoded light-theme hex
                            // literals and a Vietnamese-only aria-label — a Tier-1 defect, since
                            // the (i) rendered light-theme colours on a dark background for every
                            // consumer and its accessible name never followed locale. The Tooltip
                            // bg/text pairing mirrors the same "dark chip" convention `antdTheme.ts`
                            // already uses for antd's global Tooltip token (`colorBgSpotlight:
                            // colors.text`, `colorTextLightSolid: colors.surface`), so it stays
                            // theme-consistent instead of inventing a one-off pair.
                            <Tooltip
                                title={subtitle}
                                color={colors.text}
                                overlayInnerStyle={{
                                    color: colors.surface,
                                    fontSize: 12.5,
                                    lineHeight: 1.5,
                                    width: 340,
                                }}
                            >
                                <button
                                    type="button"
                                    aria-label={text.pageHeader.aboutThisPage}
                                    style={{
                                        display: 'inline-flex',
                                        flex: 'none',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 22,
                                        height: 22,
                                        padding: 0,
                                        border: 0,
                                        borderRadius: 999,
                                        background: colors.cta,
                                        color: colors.textOnCta,
                                        fontSize: 12,
                                        fontWeight: 800,
                                        cursor: 'help',
                                    }}
                                >
                                    i
                                </button>
                            </Tooltip>
                        )}
                        {titleExtra}
                    </Flex>
                    {subtitle && !collapsed && (
                        <Text type="secondary" style={{ color: colors.textMuted }}>
                            {subtitle}
                        </Text>
                    )}
                </div>

                {actions && (
                    <Flex align="center" gap={8} wrap="wrap" style={{ flexShrink: 0 }}>
                        {actions}
                    </Flex>
                )}
            </Flex>
        </div>
    );
}

export default PageHeader;
